import { Component, Input, Output, EventEmitter, inject, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { MaterialModule } from '@app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalsViewComponent } from '@components/modals/modals-view/modals-view.component';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';
import { Subject, takeUntil } from 'rxjs';
import { ProfileComponent} from '@pages/users/technicians/profile/profile.component';
import { Technician} from '@interfaces/technician';
import { ToastrServices } from '@services/toastr/toastr.service';
import { EmailComponent } from '@pages/users/technicians/email/email.component';
import { Router } from '@angular/router';
import { Roles, RoleLabelKeys } from '@enums/Roles';

@Component({
  selector: 'app-card-user',
  imports: [MaterialModule, TablerIconsModule, CommonModule, TranslateModule],
  standalone: true,
  templateUrl: './card-user.component.html',
  styleUrl: './card-user.component.scss'
})
export class CardUserComponent implements OnInit, OnDestroy, OnChanges {
  @Input() technician: Technician | null = null;
  // Room assigned to the technician, known from the rooms list even when nobody is connected
  @Input() assignedRoomName: string | null | undefined = null;
  @Output() technicianDeleted = new EventEmitter<void>();
  // "Elenco" view of the Tecnici page: one compact row instead of the card
  @Input() compact = false;
  private technician_api_service = inject(TechnicianApiService);
  private toastr_service = inject(ToastrServices);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private destroy$ = new Subject<void>();
  qrcode: string = "";
  // waiting = alone in the room, in_call = an operator is already with them, offline = not in a room
  status: 'waiting' | 'in_call' | 'offline' = 'offline';
  connectionNumber = 0;
  connectedRoomName: string | null = null;
  canConnect = false;
  // Translation key used for the tooltip that explains why "Entra in stanza" is disabled
  tooltipKey = '';
  private activeRoomUuid: string | null = null;

  constructor(private dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['technician']) {
      this.updateConnectionState();
    }
  }

  // Only a field technician has a room and a QR for the headset; remote technicians and the backoffice enter the panel
  get isFieldTechnician(): boolean {
    return this.technician?.roles?.id === Roles.TECHNICIAN;
  }

  get roleLabelKey(): string {
    return RoleLabelKeys[this.technician?.roles?.id ?? Roles.TECHNICIAN] ?? '';
  }

  ngOnInit(): void {
    this.updateConnectionState();
    if (this.technician && this.isFieldTechnician){
      this.technician_api_service.get_qrcode(this.technician.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.qrcode = response.data.qrcode;
          },
          error: (error) => {
            console.log(error);
          }
        });
    }
  }

  get initials(): string {
    const first = (this.technician?.name || '').trim().charAt(0);
    const last = (this.technician?.lastname || '').trim().charAt(0);
    return (first + last).toUpperCase() || '?';
  }

  get roomLabel(): string | null {
    return this.connectedRoomName ?? this.assignedRoomName ?? this.extractRoomName(this.technician);
  }

  viewFile(){
    this.showFile(this.qrcode);
  }

  showFile(base64String: string) {
    const mimeType = this.getMimeTypeDaBase64(base64String);
    const fullName = [this.technician?.name, this.technician?.lastname].filter(Boolean).join(' ');
    const imageData = {
      name: this.translate.instant('technicians_page.qr_title', { name: fullName }),
      content_type: mimeType,
      decrypted_file: base64String,
      // Lets the viewer offer "Scarica" next to "Chiudi"
      download: () => this.downloadQrcode()
    };

    this.dialog.open(ModalsViewComponent, {
      data: imageData,
      width: '100%',
      maxWidth: '420px',
      maxHeight: '90vh',
      panelClass: 'xr-dialog',
      autoFocus: false,
    });
  }

  getMimeTypeDaBase64(base64String: string): string | null {
    const match = base64String.match(/^data:(image\/[a-zA-Z0-9.+_-]+);base64,/);
    return match ? match[1] : null;
  }

  downloadQrcode() {
    if (this.qrcode) {
      const link = document.createElement('a');
      link.href = this.qrcode;
      link.download = `qrcode_${this.technician?.id || 'unknown'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.warn('Nessun QR code disponibile per il download.');
    }
  }

  openTechnician(user: Technician | null): void{
    const dialogRef = this.dialog.open(ProfileComponent, {
      data: user,
      autoFocus: false,
      width: '100%',
      maxWidth: '560px',
      panelClass: 'xr-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.deleted === true) {
        this.toastr_service.showSuccess(this.translate.instant('technicians.delete_success'));
        this.technicianDeleted.emit();
        return;
      }

      if (result?.deleted === false) {
        this.toastr_service.showError(this.translate.instant('technicians.delete_error'));
        return;
      }

      if (result) {
        this.toastr_service.showSuccess(this.translate.instant('technicians_page.toast_updated'));
        const currentTechnician = this.technician ?? undefined;
        const updatedTechnician = {
          ...(currentTechnician || {}),
          ...(result.data || {})
        } as Technician;

        if (currentTechnician) {
          updatedTechnician.connected = currentTechnician.connected;
          updatedTechnician.connectionNumber = currentTechnician.connectionNumber;
          updatedTechnician.activeRoom = currentTechnician.activeRoom ?? updatedTechnician.activeRoom ?? null;
        }

        this.technician = updatedTechnician;
        this.updateConnectionState();
      }
    }, error => {
      console.error('Errore dalla modale:', error);
      this.toastr_service.showError(this.translate.instant('technicians_page.toast_error'));
    });
  }

  sendEmail(user: Technician | null): void{
    const dialogRef = this.dialog.open(EmailComponent, {
      data: {
        "technician_email": user?.email,
        "qr_code":this.qrcode
      },
      autoFocus: false,
      width: '100%',
      maxWidth: '560px',
      panelClass: 'xr-dialog',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == 'close' || !result) {
        //
      } else {
        if (result.success) {
          this.toastr_service.showSuccess(this.translate.instant('technicians_page.toast_email_sent'));
        } else if (!result.success ) {
          this.toastr_service.showError(this.translate.instant('technicians_page.toast_email_error'));
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  connectToRoom(): void {
    if (!this.canConnect || !this.activeRoomUuid) {
      return;
    }
    this.router.navigate(['/support/room', this.activeRoomUuid]);
  }

  private updateConnectionState(): void {
    const technician = this.technician;
    const isConnected = !!technician?.connected;
    const roomName = this.extractRoomName(technician);
    const roomUuid = this.extractRoomUuid(technician);
    const rawConnectionNumber = technician?.connectionNumber ?? (isConnected && roomName ? 1 : 0);

    if (isConnected && rawConnectionNumber > 1) {
      this.status = 'in_call';
    } else if (isConnected) {
      this.status = 'waiting';
    } else {
      this.status = 'offline';
    }

    this.connectionNumber = isConnected ? Math.max(rawConnectionNumber, 1) : 0;
    this.connectedRoomName = isConnected ? roomName : null;
    this.activeRoomUuid = roomUuid || technician?.assignedRoom?.uuid || null;
    // Same rule as the Dashboard and the rooms list: the room can be entered unless a call is already in progress
    // (the panel waits there for the headset)
    this.canConnect = this.status !== 'in_call' && !!this.activeRoomUuid;

    if (this.canConnect) {
      this.tooltipKey = '';
    } else if (this.status === 'in_call') {
      // A technician and an operator are already in the call: cannot join
      this.tooltipKey = 'rooms.cannot_connect_room_busy';
    } else {
      this.tooltipKey = 'technicians_page.no_room';
    }
  }

  private extractRoomName(technician: Technician | null): string | null {
    if (!technician) {
      return null;
    }

    if (technician.activeRoom && technician.activeRoom.name) {
      return technician.activeRoom.name;
    }

    const directRoom = (technician as any).room;
    if (directRoom && directRoom.name) {
      return directRoom.name;
    }

    const camelCase = (technician as any).roomName;
    if (typeof camelCase === 'string' && camelCase.trim()) {
      return camelCase;
    }

    const snakeCase = (technician as any).room_name;
    if (typeof snakeCase === 'string' && snakeCase.trim()) {
      return snakeCase;
    }

    const connectedRoomName = (technician as any).connectedRoomName;
    if (typeof connectedRoomName === 'string' && connectedRoomName.trim()) {
      return connectedRoomName;
    }

    return null;
  }

  private extractRoomUuid(technician: Technician | null): string | null {
    if (!technician) {
      return null;
    }

    if (technician.activeRoom && technician.activeRoom.uuid) {
      return technician.activeRoom.uuid;
    }

    const directRoom = (technician as any).room;
    if (directRoom && typeof directRoom.uuid === 'string' && directRoom.uuid.trim()) {
      return directRoom.uuid;
    }

    const camelCase = (technician as any).roomUuid;
    if (typeof camelCase === 'string' && camelCase.trim()) {
      return camelCase;
    }

    const snakeCase = (technician as any).room_uuid;
    if (typeof snakeCase === 'string' && snakeCase.trim()) {
      return snakeCase;
    }

    const connectionId = (technician as any).connectionId;
    if (typeof connectionId === 'string' && connectionId.trim()) {
      return connectionId;
    }

    return null;
  }
}
